import {CloseIcon, WarningIcon} from 'native-base';
import {
  DeliveryDetailsAction,
  useDeliveryDetailsContext,
} from '@buncha/driver/hooks/deliveryDetails';
import {Modal, StyleSheet} from 'react-native';
import {OnfleetBarcode, OnfleetBarcodeData} from '@buncha/config/onfleet/types';
import React, {Fragment, useCallback, useMemo, useRef, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Barcode} from 'scandit-react-native-datacapture-barcode';
import BarcodeScanIcon from '@buncha/components/core/icon/BarcodeScanIcon';
import {CustomText} from '@buncha/components/core/text';
import {HBox} from '@buncha/components/core/view';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import Scanner from '@buncha/components/composites/Scanner';
import {ScannerMode} from '@buncha/components/composites/Scanner/type';
import {Touchable} from '@buncha/components/core/touchable';
import {useGetLocale} from '@buncha/localization/hooks';

interface VerifyBarcodecellProps {
  requestedBarcodes: OnfleetBarcodeData[];
}

const VerifyBarcodesCell = (props: VerifyBarcodecellProps) => {
  const {requestedBarcodes} = props;
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const {state, dispatch} = useDeliveryDetailsContext();
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const scannedBarcodes = useRef<OnfleetBarcode[]>([]);

  const mandatoryRemainingBarcodesCount = useMemo(() => {
    return requestedBarcodes.filter(
      barcode =>
        barcode.blockCompletion &&
        !state.barcodes.find(b => b.data === barcode.data),
    )?.length;
  }, [requestedBarcodes, state.barcodes]);

  const remainingBarcodesCount = useMemo(() => {
    return requestedBarcodes.filter(
      barcode => !state.barcodes.find(b => b.data === barcode.data),
    )?.length;
  }, [requestedBarcodes, state.barcodes]);

  const getCellText = useMemo(() => {
    if (remainingBarcodesCount === 0) return body?.allScanned;
    return body?.codesLeft?.replace(
      '{remainingBarcodesCount}',
      remainingBarcodesCount,
    );
  }, [body?.allScanned, body?.codesLeft, remainingBarcodesCount]);

  const toggleBarcodeModal = useCallback(() => {
    setBarcodeModalOpen(s => !s);
  }, []);

  const handleOnScan = useCallback(
    (barcode: Barcode, allScanned?: boolean) => {
      if (!barcode.data) return;
      scannedBarcodes.current = [
        ...scannedBarcodes.current,
        {
          data: barcode.data,
          symbology: barcode.symbology,
          wasRequested: Boolean(
            requestedBarcodes.find(b => b.data === barcode.data)
              ?.blockCompletion,
          ),
        },
      ];
      dispatch?.(DeliveryDetailsAction.setBarcodes(scannedBarcodes.current));
      if (allScanned) {
        Analytics.eventWithProps('barcode scanned', {
          source: 'Confirm Delivery Page',
          countOfBarcodes: state.barcodes.length,
        });
        toggleBarcodeModal();
      }
    },
    [dispatch, requestedBarcodes, state.barcodes.length, toggleBarcodeModal],
  );

  return (
    <Fragment>
      <Touchable
        style={[
          styles.parentBox,
          {
            borderColor: theme?.text.gray[100],
            backgroundColor: !remainingBarcodesCount
              ? theme?.background.blue[100]
              : theme?.background.white[100],
          },
        ]}
        onPress={toggleBarcodeModal}>
        <If
          condition={
            Boolean(props.requestedBarcodes?.length) &&
            mandatoryRemainingBarcodesCount
          }>
          <WarningIcon
            style={styles.warningIcon}
            color={theme?.icon.red[100]}
          />
        </If>
        <BarcodeScanIcon
          height={24}
          width={24}
          style={styles.icon}
          viewBox="0 0 14 14"
          fill={
            remainingBarcodesCount
              ? theme?.icon?.blue?.[300]
              : theme?.icon?.white?.[100]
          }
        />

        <HBox style={styles.addedContainer}>
          <CustomText
            style={[
              styles.text,
              {
                color: remainingBarcodesCount
                  ? theme?.text.blue[300]
                  : theme?.text.white[100],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {getCellText}
          </CustomText>
        </HBox>
      </Touchable>
      <If condition={barcodeModalOpen}>
        <Modal visible={barcodeModalOpen} onRequestClose={toggleBarcodeModal}>
          <Touchable
            style={styles.crossIconContainer}
            onPress={toggleBarcodeModal}>
            <CloseIcon size={25} />
          </Touchable>
          <Scanner
            setScannerEnabled={setScannerEnabled}
            mode={ScannerMode.continuousScan}
            scannerEnabled={scannerEnabled}
            expectedBarcodes={requestedBarcodes.map(b => b.data)}
            scannedBarcodes={scannedBarcodes.current.map(b => b.data)}
            onScan={handleOnScan}
          />
        </Modal>
      </If>
    </Fragment>
  );
};

export default React.memo(VerifyBarcodesCell);

const styles = StyleSheet.create({
  parentBox: {
    justifyContent: 'center',
    paddingHorizontal: 2,
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
    borderWidth: 0.5,
  },
  text: {
    marginTop: 5,
  },
  icon: {
    alignSelf: 'center',
  },
  crossIconContainer: {
    position: 'absolute',
    top: 35,
    zIndex: 8,
    right: 20,
    padding: 5,
    borderRadius: 50,
  },
  warningIcon: {position: 'absolute', right: 2, top: 2},
  addedContainer: {justifyContent: 'center', alignItems: 'center', gap: 2},
});
